/**
 * Dock Snap Resolver
 * Shared drop-target geometry for every dockable surface (sidepanels via
 * usePanelDrag, floating panels, and the toolbar rail). A drop resolves to a
 * { side, index } decision understood by useDockLayoutStore:
 *
 * 1. Screen-edge bands (outermost 80px, sidepanel parity) dock the entry into
 *    the OUTERMOST slot of that edge — the slot the band visually covers.
 *    Strip arrays render sequentially (left/top: index 0 sits at the screen
 *    edge; right/bottom: the array end sits at the screen edge), so left/top
 *    bands report index 0 and right/bottom bands report DOCK_APPEND_INDEX
 *    (the store clamps it to the strip length). Full-edge band overlay
 *    (overlayRect = null).
 * 2. Entry-relative targets: hovering the leading/trailing half of a docked
 *    entry (panel or toolbar rail) — or the band right beside it — inserts
 *    BETWEEN entries, so a panel can be dropped to the left or right of
 *    another panel, beside the toolbar, etc. Strip arrays render in DOM
 *    order (left→right / top→bottom on every edge), so leading is always
 *    the left/upper side of the entry. These hits report the exact highlight
 *    geometry in overlayRect so consumers can draw the insert overlay at the
 *    hovered slot instead of the edge band.
 *
 * The resolver stays DOM-free: callers feed it the entry rectangles.
 */

import { DockSide, DockTarget } from '../store/useDockLayoutStore';

/** Outermost band thickness in px (sidepanel snap parity) */
export const DOCK_EDGE_MARGIN = 80;

/** Sentinel index for "append to the strip end": the store clamps indexes
    past the end, so this lands on the final slot — the screen-edge slot of
    right/bottom strips, where the edge band overlay is drawn. */
export const DOCK_APPEND_INDEX = Number.MAX_SAFE_INTEGER;

/** A docked entry's hit geometry, captured once per pointermove by callers */
export interface DockEntryRect {
    id: string;
    left: number;
    top: number;
    width: number;
    height: number;
}

export interface ResolvedDockTarget {
    /** Null while the pointer is over no zone (entry floats / cancels) */
    side: DockSide | null;
    /** Insert position inside the side's strip (0 = outermost edge) */
    index: number;
    /** Exact highlight geometry for entry-relative hits (the slot beside /
        between docked entries). Null for screen-edge band hits, which use the
        full-edge overlay styling. */
    overlayRect: DockEntryRect | null;
}

/** Registry of the currently docked entries' rectangles, in STRIP ORDER
    (outermost first). Updated while a drag is live by the dragging surface;
    every consumer reads it, so relative snapping works across instances
    without prop plumbing. For the per-edge flattening the caller lists rects
    per edge in strip order; this module only consumes the flat array and
    derives insert indexes from positions inside it. */
let liveEntryRects: DockEntryRect[] = [];

/** Optional per-entry strip side, registered by callers alongside the rects:
    the edge the entry is docked to per useDockLayoutStore. Read live during
    hit testing so the insert axis, reported side and strip index follow the
    real layout even for compact entries like the toolbar rail. */
let liveEntrySide: ((entryId: string) => DockSide | undefined) | null = null;

export const setLiveDockEntryRects = (
    rects: DockEntryRect[],
    getEntrySide?: (entryId: string) => DockSide | undefined,
): void => {
    liveEntryRects = rects;
    liveEntrySide = getEntrySide ?? null;
};

export const clearLiveDockEntryRects = (): void => {
    liveEntryRects = [];
    liveEntrySide = null;
};

/** Thickness of the insert band drawn beside/inside a docked entry (px) */
export const DOCK_INSERT_BAND = 56;

/**
 * Resolve a pointer position to a dock target.
 * `selfId` is excluded from relative splitting (you cannot dock beside
 * yourself mid-move; the store also treats that as a no-op).
 *
 * The reported { side, index } is always relative to the hit entry's OWN
 * side strip (liveEntrySide supplies each entry's edge), so callers can feed
 * it straight into useDockLayoutStore.dockEntry even though the registered
 * rect list spans multiple edges.
 */
export function resolveDockTarget(
    x: number,
    y: number,
    selfId?: string,
): ResolvedDockTarget {
    const w = window.innerWidth;
    const h = window.innerHeight;

    // 1. Outermost screen-edge bands dock into the slot the band covers:
    //    the outermost slot for left/top strips, the innermost (final) slot
    //    for right/bottom strips — matching where the band is drawn.
    //    Full-edge overlay: no rect.
    if (x < DOCK_EDGE_MARGIN)
        return { side: 'left', index: 0, overlayRect: null };
    if (x > w - DOCK_EDGE_MARGIN)
        return { side: 'right', index: DOCK_APPEND_INDEX, overlayRect: null };
    if (y < DOCK_EDGE_MARGIN)
        return { side: 'top', index: 0, overlayRect: null };
    if (y > h - DOCK_EDGE_MARGIN)
        return { side: 'bottom', index: DOCK_APPEND_INDEX, overlayRect: null };

    // 2. Entry-relative targets: hover INSIDE an entry (leading/trailing half
    //    along its strip axis) or in the band directly BESIDE it. Strips flow
    //    in DOM order (left→right, top→bottom on every edge), so leading /
    //    before is always the entry's left/upper side. The strip side comes
    //    from the dock layout store via liveEntrySide, with the rect aspect
    //    ratio as a fallback.
    const hit = liveEntryRects.find((rect) => {
        if (rect.id === selfId) return false;
        const expand = DOCK_EDGE_MARGIN;
        return (
            x >= rect.left - (x < rect.left ? expand : 0) &&
            x < rect.left + rect.width + (x >= rect.left + rect.width ? expand : 0) &&
            y >= rect.top - (y < rect.top ? expand : 0) &&
            y < rect.top + rect.height + (y >= rect.top + rect.height ? expand : 0)
        );
    });
    if (!hit) return { side: null, index: 0, overlayRect: null };

    const stripSide = liveEntrySide?.(hit.id);
    const verticalStrip =
        stripSide === undefined
            ? hit.height >= hit.width
            : stripSide === 'left' || stripSide === 'right';
    const strip: DockSide = stripSide ?? (verticalStrip ? 'left' : 'top');

    // Insert position inside the hit entry's own side strip: count the
    // same-side entries preceding it in the registered strip-ordered list
    const sameSideIndex = (entryId: string) =>
        liveEntryRects
            .slice(0, liveEntryRects.findIndex((r) => r.id === entryId))
            .filter((r) => (liveEntrySide?.(r.id) ?? strip) === strip).length;

    if (verticalStrip) {
        // Inside hits split along the strip axis (top half = before, matching
        // the vertical stacking); outside hits split by which side band the
        // pointer is in (left band = before, right band = after)
        const inside = x >= hit.left && x < hit.left + hit.width;
        const before = inside
            ? y < hit.top + hit.height / 2
            : x < hit.left;
        return {
            side: strip,
            index: sameSideIndex(hit.id) + (before ? 0 : 1),
            overlayRect: {
                ...hit,
                left: inside
                    ? hit.left - DOCK_INSERT_BAND / 2
                    : before
                      ? hit.left - DOCK_INSERT_BAND
                      : hit.left + hit.width,
                width: inside ? hit.width + DOCK_INSERT_BAND : DOCK_INSERT_BAND,
                top: inside
                    ? before
                        ? hit.top
                        : hit.top + hit.height - DOCK_INSERT_BAND
                    : hit.top,
                height: inside ? DOCK_INSERT_BAND : hit.height,
            },
        };
    }

    // Horizontal strip: inside hits split left/right along the strip axis,
    // outside hits by which vertical band the pointer is in
    const inside = y >= hit.top && y < hit.top + hit.height;
    const before = inside
        ? x < hit.left + hit.width / 2
        : y < hit.top;
    return {
        side: strip,
        index: sameSideIndex(hit.id) + (before ? 0 : 1),
        overlayRect: {
            ...hit,
            top: inside
                ? before
                    ? hit.top
                    : hit.top + hit.height - DOCK_INSERT_BAND
                : before
                  ? hit.top - DOCK_INSERT_BAND
                  : hit.top + hit.height,
            height: inside ? DOCK_INSERT_BAND : hit.height,
            left: inside
                ? before
                    ? hit.left
                    : hit.left + hit.width - DOCK_INSERT_BAND
                : hit.left,
            width: inside ? DOCK_INSERT_BAND : hit.width,
        },
    };
}
